import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { fetchBalances } from '@/api'
import { queryKeys } from '@/queries/keys'

import { coinAmountFromBalances, computeDelegationState } from '../utils'
import { useBecomeValidatorBase } from './use-become-validator-base'

export function useBecomeValidatorAmountPayload(
  address: string | null,
  amountAtomic?: string,
) {
  const base = useBecomeValidatorBase()

  const { data: walletBalances, isLoading: isLoadingWalletBalances } = useQuery(
    {
      queryKey: queryKeys.bank.balances(address),
      enabled: Boolean(address),
      queryFn: async () => fetchBalances(address!),
      staleTime: 30_000,
    },
  )

  const walletBondAtomic = useMemo(() => {
    return coinAmountFromBalances(walletBalances?.balances, base.bondDenom)
  }, [walletBalances, base.bondDenom])

  const haveAtomic = useMemo(() => {
    try {
      if (amountAtomic) {
        const amt = BigInt(amountAtomic)
        const wallet = BigInt(walletBondAtomic)
        if (amt <= wallet) return amountAtomic
        return '0'
      }
    } catch {
      // fallthrough to no entered amount
    }
    // when nothing entered, behave like shards flow (0 selected)
    return '0'
  }, [amountAtomic, walletBondAtomic])

  const { remainingAtomic, hasEnoughDelegation, remainingColorClass } = useMemo(
    () => computeDelegationState(haveAtomic, base.minimalDelegationAmount),
    [haveAtomic, base.minimalDelegationAmount],
  )

  return {
    // base
    bondDenom: base.bondDenom,
    minimalDelegationAmount: base.minimalDelegationAmount,

    // amount-specific
    walletBondAtomic,
    isLoadingWalletBalances,

    // computed
    remainingAtomic,
    hasEnoughDelegation,
    remainingColorClass,
  }
}
